import React, { useState, useEffect, memo, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import TrimbleMaps from '@trimblemaps/trimblemaps-js'
import { lineString } from '@turf/helpers'
import bbox from '@turf/bbox'

import { Spinner } from '../../components'
import useStyles from './useStyles'
import { useTheme } from '../../theming/ThemeProvider'
import { getLaneColor } from '../../tools/objectMapValues'
import { getTractorIcon } from '../../tools/dataParse'
import './trimblemaps-2.3.1.css'

const MapComponent = ({ routes, handleInvalidRoute, tractorPosition, mapId }) => {
  const [loading, setLoading] = useState(true)
  const { theme } = useTheme()
  const styles = useStyles(theme)

  const map = useRef(null)
  const mapRoutes = useRef([])
  const markerLayer = useRef([])
  const routesPoints = useRef([])

  const centerMap = useCallback(() => {
    const line = lineString(routesPoints.current)
    const boundingBox = bbox(line)
    map.current.fitBounds(new TrimbleMaps.LngLatBounds(
      [boundingBox[0], boundingBox[1]],
      [boundingBox[2], boundingBox[3]]
    ), {
      padding: 60
    })
  }, [])

  const getOriginIcon = () => {
    const content = `<svg style="transform: translate(0px,-8px)" height="24" width="24" id="filter_ship" xmlns="http://www.w3.org/2000/svg" width="173.98" height="253.015" viewBox="0 0 173.98 253.015">
    <path id="Path_543" data-name="Path 543" d="M241.863,43.327A85.861,85.861,0,0,0,168.9.02c-1.295-.027-2.6-.027-3.893,0A85.862,85.862,0,0,0,92.052,43.327,88.084,88.084,0,0,0,90.9,130.362L153.644,245.2c.028.051.056.1.085.152a15.25,15.25,0,0,0,26.457,0c.029-.05.057-.1.085-.152l62.741-114.839a88.086,88.086,0,0,0-1.15-87.035Zm-74.906,71.32a35.58,35.58,0,1,1,35.58-35.58A35.621,35.621,0,0,1,166.958,114.647Z" transform="translate(-79.968 0)"/>
  </svg>
  `
    const svgContent = document.createElement('div')
    // svgContent.title = 'SVG content marker'
    svgContent.innerHTML = content
    return svgContent
  }

  const getDestinationIcon = () => {
    const content = `<svg style="transform: translate(7px,-8px)" height="24" width="24" id="filter_pointer" xmlns="http://www.w3.org/2000/svg" width="161.832" height="253.015" viewBox="0 0 161.832 253.015">
    <g id="Group_2633" data-name="Group 2633" transform="translate(0 0)">
      <path id="Path_542" data-name="Path 542" d="M249.5,12.34a7.608,7.608,0,0,0-8.258,1.4c-1.324,1.167-25.869,21.971-64.12-.909C151.969-2.208,131.1-1.327,118.029,2.061c-14.212,3.683-22.387,10.887-23.272,11.7a7.681,7.681,0,0,0-2.5,5.67v225.9a7.683,7.683,0,1,0,15.367,0V102.375c7.233-4.871,29.731-16.266,61.605,2.8,16.366,9.789,30.916,12.835,42.687,12.835a65.358,65.358,0,0,0,16.4-2.061c14.212-3.683,22.386-10.887,23.272-11.7a7.683,7.683,0,0,0,2.5-5.67V19.429A7.7,7.7,0,0,0,249.5,12.34ZM238.724,94.8c-7.293,4.949-29.674,16.291-61.6-2.809C160.753,82.2,146.2,79.152,134.432,79.152a65.358,65.358,0,0,0-16.4,2.061,64.5,64.5,0,0,0-10.4,3.665V23.222c7.238-4.872,29.737-16.261,61.605,2.8,25.148,15.042,46.013,14.161,59.089,10.774a64.516,64.516,0,0,0,10.4-3.665V94.8Z" transform="translate(-92.259 0)"/>
    </g>
  </svg>`
    const svgContent = document.createElement('div')
    // svgContent.title = 'SVG content marker'
    svgContent.innerHTML = content
    return svgContent
  }

  const getStopIcon = (index) => {
    const content = `<svg width="34" height="34" xmlns="http://www.w3.org/2000/svg">' +
                '<circle id="svgCircle" stroke="#FFF" fill="#35a310" cx="16" cy="16" r="16" />' +
                '<text id="svgText" name="stopNumber" x="16" y="20" font-size="10pt" font-family="arial" font-weight="bold" text-anchor="middle" fill="#FFF" textContent="1">${index}</text>' +
                '</svg>`
    const svgContent = document.createElement('div')
    svgContent.title = 'SVG content marker'
    svgContent.innerHTML = content
    return svgContent
  }

  const setStopsMarkerIcon = useCallback((index, stopsCount) => {
    if (index === 1) return getOriginIcon()
    else if (index !== stopsCount && index !== 1) return getStopIcon(index)
    else return getDestinationIcon()
  }, [])

  const addRoutes = useCallback((_routes) => {
    mapRoutes.current.forEach(route => route.remove())
    mapRoutes.current = []
    routesPoints.current = []
    let routesDrawn = 0
    const filteredRoutes = _routes.filter(item => item[0][0] !== null && item[0][1] !== null && item[1][0] !== null && item[1][1] !== null)
    filteredRoutes.forEach((route, index) => {
      const newRoute = new TrimbleMaps.Route({
        routeId: `route${index}`,
        routeColor: getLaneColor(index),
        stops: route.map((stop, stopsIndex) => {
          const newPoint = [stop[1], stop[0]]
          routesPoints.current = [
            ...routesPoints.current,
            newPoint
          ]
          const tempMarker = new TrimbleMaps.Marker({ element: setStopsMarkerIcon(stopsIndex + 1, route.length) })
            .setLngLat(newPoint)
            .addTo(map.current)
          markerLayer.current.push(tempMarker)
          return new TrimbleMaps.LngLat(stop[1], stop[0])
        })
      })
      newRoute._startIcon = null
      newRoute._endIcon = null
      newRoute._stopIcon = null
      newRoute.on('route', () => {
        routesDrawn++
        if (routesDrawn === filteredRoutes.length) {
          centerMap()
          setLoading(false)
        }
      })
      mapRoutes.current = [
        ...mapRoutes.current,
        newRoute
      ]
      newRoute.addTo(map.current)
      map.current.stop()
    })
  }, [centerMap, setStopsMarkerIcon])

  const addTractorIcon = useCallback((_tractorPosition) => {
    const icon = document.createElement('div')
    icon.style.width = '0'
    icon.style.height = '0'
    icon.insertAdjacentHTML('afterbegin', getTractorIcon())
    const marker = new TrimbleMaps.Marker({
      element: icon,
      draggable: false
    })
    marker.setLngLat([_tractorPosition.longitude, _tractorPosition.latitude])
    marker.addTo(map.current)
  }, [])

  const buildMap = useCallback((_routes, _tractorPosition) => {
    if (_routes.length) {
      markerLayer.current.forEach((marker) => marker.remove())
      addRoutes(_routes)
    }
    if (_tractorPosition) {
      addTractorIcon(_tractorPosition)
    }
  }, [addRoutes, addTractorIcon])

  const createMap = useCallback(() => {
    TrimbleMaps.APIKey = '74B8CA8A206E0E4F8AA945EC2BBEE8A4'

    return map.current || new TrimbleMaps.Map({
      container: mapId,
      center: new TrimbleMaps.LngLat(-96, 35),
      zoom: 3
    })
  }, [mapId])

  useEffect(() => {
    setLoading(true)
    map.current = createMap()
    try {
      buildMap(routes, tractorPosition)
    } catch (e) {
      map.current.on('load', () => {
        buildMap(routes, tractorPosition)
      })
    }
  }, [routes, tractorPosition, createMap, buildMap])

  return (
    <>
      <div
        id={mapId}
        style={{
          width: '100%',
          height: '100%'
        }}
      />
      {loading ? <Spinner containerStyle={styles.loadingContainer} /> : null}
    </>
  )
}

MapComponent.propTypes = {
  routes: PropTypes.array,
  tractorPosition: PropTypes.object,
  handleInvalidRoute: PropTypes.func,
  mapId: PropTypes.string
}

MapComponent.defaultProps = {
  routes: [],
  tractorPosition: null
}

const routePropIsEqual = (prevProps, nextProps) => {
  return JSON.stringify(prevProps.routes) === JSON.stringify(nextProps.routes) &&
    JSON.stringify(prevProps.tractorPosition) === JSON.stringify(nextProps.tractorPosition)
}

export default memo(MapComponent, routePropIsEqual)
